// Boot orchestrator — Task 4.12.
//
// Resolves the shareId from the URL, fetches manifest+peaks, instantiates the
// Player, wires its events into the shared state store, and mounts every UI
// panel into its `#share-*` host. Renders an error screen on invalid path,
// 404, or load failure. Auto-boots on page load unless the `__SHARE_WEB_TEST__`
// global is set (tests import this module and drive `boot()` themselves).

// Style imports. Cascade order matters — desktop CSS comes FIRST (provides the
// source-of-truth selectors for .app/.rh-*/.tr-*/.brand/.tb-*/.sl-*), then web
// CSS LAYERS overrides on top. Web layout.css overrides .app grid template +
// min-width:0 (desktop sets 1180px), web web-only-overrides adds web-specific
// modifiers, etc. Tokens.css MUST be loaded first so CSS custom props are
// available to all subsequent rules.
import './styles/tokens.css';

// Desktop CSS — source of truth (still reused via @desktop-css alias).
import '@desktop-css/rehearsal/player-panel-shell.css';
import '@desktop-css/rehearsal/controls-column.css';
import '@desktop-css/rehearsal/waves-column.css';
import '@desktop-css/rehearsal/takes-sidebar.css';
import '@desktop-css/app-shell.css';
import '@desktop-css/topbar.css';
// Vendored desktop CSS — these three legacy files were deleted on desktop during
// the design-system migration (song-header → bw-song-header `3a47150`, transport →
// bw-transport `d228e60`, setlist-panel `0374b6c`). share-web still renders the
// plain desktop classes, so we vendor the pre-deletion CSS locally to decouple
// from the volatile desktop paths (full CSS decoupling / DS adoption = sub-project 4).
import './styles/desktop/song-header.css';
import './styles/desktop/transport.css';
import './styles/desktop/setlist-panel.css';

// Web overrides — must come AFTER desktop CSS to win cascade.
import './styles/layout.css';
import './styles/topbar.css';
import './styles/promo-footer.css';
import './styles/promo-modal.css';
import './styles/web-only-overrides.css';
import './styles/setlist-sidebar.css';
import './styles/share-modal.css';
import './styles/sections.css';
import './styles/library.css';
import './styles/admin.css';
import './styles/dashboard.css';

import { parseShareIdFromPath, isLibraryPath, isSetlistPath, isAdminPath, isLoginPath, isDashboardPath } from './router.js';
import { fetchMe } from './me.js';
import { roleHome } from './role-home.js';
import { fetchTeams, fetchTeamDetail, setTeamQuota, revokeShare, deactivateUser, removeMember } from './admin.js';
import { mountAdminView } from './ui/admin-view.js';
import { fetchLibrary } from './library.js';
import { mountLibraryView } from './ui/library-view.js';
import { fetchDashboard } from './dashboard.js';
import { mountDashboardView } from './ui/dashboard-view.js';
import { fetchPublishedSetlist } from './setlist.js';
import { mountSetlistView } from './ui/setlist-view.js';
import {
  loadManifest,
  loadPeaks,
  loadPerTrackPeaks,
} from './manifest-loader.js';
import { mountLoginGate } from './ui/login-gate.js';
import { t } from './ui/i18n.js';
import { createPlayer, type Player } from './player.js';
import { getState, setState, subscribe } from './state.js';
import { mountTopbar } from './ui/topbar.js';
import { mountPromoFooter } from './ui/promo-footer.js';
import { mountPromoModal } from './ui/promo-modal.js';
import { mountTakesSidebar } from './ui/takes-sidebar.js';
import { mountSetlistSidebar } from './ui/setlist-sidebar.js';
import { mountPlayerPanel } from './ui/player-panel.js';
import { mountShareModal } from './ui/share-modal.js';
import { registerServiceWorker } from './sw-register.js';
import { mountSectionHandlers } from './ui/section-handlers.js';
import { mountLoopIndicator } from './ui/section-loop-binding.js';

// In production the CDN serves `/shares/<shareId>/...`. On localhost (both
// `vite dev` and `vite preview`) the dev-fixtures plugin (see `vite.config.ts`)
// mirrors that layout under `/dev-fixtures` so the SPA can be driven locally
// without touching the live CDN. The acceptance procedure uses `npm run preview`
// to validate SW caching against the production build output — hostname-based
// detection ensures preview mode also picks up local fixtures.
// See `apps/share-web/README.md`.
const DEFAULT_CDN_BASE = (() => {
  if (typeof location !== 'undefined') {
    const host = location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') {
      return `${location.origin}/dev-fixtures`;
    }
  }
  return 'https://cdn.bandway.app';
})();

export interface BootOpts {
  cdnBase?: string;
  audioContextFactory?: () => AudioContext;
  /**
   * Override for audio fetches (the player calls this to load each track's
   * URL). Manifest and peaks are fetched via the global `fetch` (which tests
   * stub through `vi.stubGlobal`). Defaults to `globalThis.fetch`.
   */
  fetchImpl?: typeof fetch;
  /** Client-side redirect; injectable for tests. Defaults to location.assign. */
  navigate?: (path: string) => void;
}

export interface BootSuccess {
  unmount: () => void;
}

export interface BootError {
  error: { kind: string; message: string };
}

export type BootResult = BootSuccess | BootError;

function byId(id: string): Element {
  const el = document.getElementById(id);
  if (!el) {
    throw new Error(`mount point #${id} not found`);
  }
  return el;
}

/**
 * Switch the shell into a full-bleed, non-player view (library / setlist /
 * login gate). The index.html shell ships with the player-grid scaffolding
 * (#share-topbar / #share-rail / #share-console placeholders) which would
 * otherwise reserve grid tracks and box the view into a single cell. We clear
 * those children and tag `<body data-view=…>` so `library.css` can collapse
 * the grid and hide the player chrome. `data-ready` is reset because the
 * player-only ready flag is meaningless here. (B-064)
 */
function enterFullBleedView(view: 'library' | 'setlist' | 'login' | 'admin' | 'dashboard', appHost: HTMLElement): void {
  appHost.innerHTML = '';
  appHost.setAttribute('data-ready', 'false');
  if (typeof document !== 'undefined' && document.body) {
    document.body.setAttribute('data-view', view);
  }
}

/** Undo {@link enterFullBleedView} — used by the view's unmount(). */
function exitFullBleedView(): void {
  if (typeof document !== 'undefined' && document.body) {
    document.body.removeAttribute('data-view');
  }
}

function renderInvalidShareId(host: Element): void {
  host.textContent = '';
  const wrap = document.createElement('div');
  wrap.className = 'error-screen';
  wrap.dataset.errorKind = 'invalid-share-id';
  const h = document.createElement('h1');
  h.textContent = 'Invalid share link';
  const p = document.createElement('p');
  p.textContent = 'This link is malformed. Please check it and try again.';
  wrap.appendChild(h);
  wrap.appendChild(p);
  host.appendChild(wrap);
}

function renderErrorScreen(
  host: Element,
  error: { kind: string; message: string },
): void {
  host.textContent = '';
  const wrap = document.createElement('div');
  wrap.className = 'error-screen';
  wrap.dataset.errorKind = error.kind;
  const h = document.createElement('h1');
  h.textContent =
    error.kind === 'not-found'
      ? 'No longer available'
      : 'Could not load share';
  const p = document.createElement('p');
  p.textContent = error.message;
  wrap.appendChild(h);
  wrap.appendChild(p);
  host.appendChild(wrap);
}

function isNotFoundError(message: string): boolean {
  return /404|not.found/i.test(message);
}

/** Thrown when the login gate has already been mounted; callers must not render the error screen. */
class GateShownError extends Error {
  constructor() { super('gate-shown'); this.name = 'GateShownError'; }
}

export async function boot(opts: BootOpts = {}): Promise<BootResult> {
  // Fire-and-forget Service Worker registration. On first install, in-flight
  // fetches bypass the SW entirely (browser semantics) — caching kicks in
  // from the second navigation onward, after `clients.claim()`. We don't
  // block boot on activation; that would add latency for no benefit. Errors
  // are swallowed inside registerServiceWorker — SW is progressive
  // enhancement, never required.
  void registerServiceWorker();

  const cdnBase = opts.cdnBase ?? DEFAULT_CDN_BASE;
  const fetchImpl =
    opts.fetchImpl ?? globalThis.fetch.bind(globalThis);

  const appHost = document.getElementById('app');
  if (!appHost) {
    throw new Error('boot: #app element not found');
  }

  const navigate = opts.navigate ?? ((path: string) => { if (typeof location !== 'undefined' && typeof location.assign === 'function') location.assign(path); });

  // Standalone login (/login): external landing's "Sign in" links here.
  if (isLoginPath(location.pathname)) {
    enterFullBleedView('login', appHost);
    const API_BASE = '';
    const me = await fetchMe(API_BASE);
    if (me.ok) { navigate(roleHome(me.me)); return { unmount: () => { appHost.innerHTML = ''; exitFullBleedView(); } }; }
    mountLoginGate(appHost, {
      teamName: null,
      apiBase: API_BASE,
      context: 'portal',
      // Route through the roleHome seam (not a literal path) so W2's admin
      // branch picks up post-OTP logins too. Just authenticated → fetchMe succeeds.
      onAuthed: async () => { const after = await fetchMe(API_BASE); navigate(after.ok ? roleHome(after.me) : '/library'); },
    });
    return { unmount: () => { appHost.innerHTML = ''; exitFullBleedView(); } };
  }

  // Root (/): authed → role home; else → login.
  if (location.pathname === '/' || location.pathname === '') {
    const API_BASE = '';
    const me = await fetchMe(API_BASE);
    navigate(me.ok ? roleHome(me.me) : '/login');
    return { unmount: () => {} };
  }

  // Team-library view (/library): cookie-authed aggregate, OTP gate on 401.
  if (isLibraryPath(location.pathname)) {
    // These views replace the rehearsal player. Clear the shell slots
    // (#share-topbar/#share-rail/#share-console placeholders from index.html)
    // so library/login mount full-bleed, and flag the body so library.css can
    // collapse the player grid and hide the player chrome (B-064).
    enterFullBleedView('library', appHost);
    const API_BASE = '';
    const renderLibrary = async (): Promise<void> => {
      const result = await fetchLibrary(API_BASE);
      if (result.ok) {
        mountLibraryView(appHost, {
          teams: result.teams,
          navigate: (path) => { if (typeof location !== 'undefined') location.assign(path); },
        });
        return;
      }
      if (result.needsAuth) {
        mountLoginGate(appHost, {
          teamName: null,
          apiBase: API_BASE,
          context: 'portal',
          onAuthed: async () => { appHost.innerHTML = ''; await renderLibrary(); },
        });
        return;
      }
      appHost.innerHTML = `<p class="library-error">${t('library.loadError')}</p>`;
    };
    await renderLibrary();
    return { unmount: () => { appHost.innerHTML = ''; exitFullBleedView(); } };
  }

  // Admin view (/admin): cookie-session-authed (admin = email ∈ ADMIN_EMAILS);
  // loads on mount, not-authorized → /login. Visual styling is W2b.
  if (isAdminPath(location.pathname)) {
    enterFullBleedView('admin', appHost);
    const API_BASE = '';
    mountAdminView(appHost, {
      deps: {
        fetchTeams: () => fetchTeams(API_BASE),
        fetchTeamDetail: (tid) => fetchTeamDetail(tid, API_BASE),
        setTeamQuota: (tid, l) => setTeamQuota(tid, l, API_BASE),
        revokeShare: (s) => revokeShare(s, API_BASE),
        deactivateUser: (u, d) => deactivateUser(u, d, API_BASE),
        removeMember: (tid, u) => removeMember(tid, u, API_BASE),
      },
    });
    return { unmount: () => { appHost.innerHTML = ''; exitFullBleedView(); } };
  }

  // Team dashboard (/dashboard): cookie-authed read-only team overview, OTP gate on 401.
  if (isDashboardPath(location.pathname)) {
    enterFullBleedView('dashboard', appHost);
    const API_BASE = '';
    const renderDashboard = async (): Promise<void> => {
      const result = await fetchDashboard(API_BASE);
      if (result.ok) {
        mountDashboardView(appHost, {
          data: { team: result.team, role: result.role, joinCode: result.joinCode, members: result.members },
          navigate: (path) => { if (typeof location !== 'undefined') location.assign(path); },
        });
        return;
      }
      if (result.needsAuth) {
        mountLoginGate(appHost, {
          teamName: null,
          apiBase: API_BASE,
          context: 'portal',
          onAuthed: async () => { appHost.innerHTML = ''; await renderDashboard(); },
        });
        return;
      }
      appHost.innerHTML = `<p class="library-error">${t('dashboard.loadError')}</p>`;
    };
    await renderDashboard();
    return { unmount: () => { appHost.innerHTML = ''; exitFullBleedView(); } };
  }

  // Published-setlist view (/setlist/:id): cookie-authed, OTP gate on 401.
  const setlistMatch = isSetlistPath(location.pathname);
  if (setlistMatch) {
    enterFullBleedView('setlist', appHost);
    const API_BASE = '';
    const renderSetlist = async (): Promise<void> => {
      const result = await fetchPublishedSetlist(setlistMatch.setlistId, API_BASE);
      if (result.ok) {
        mountSetlistView(appHost, {
          setlist: result.setlist,
          items: result.items,
          navigate: (path) => { if (typeof location !== 'undefined') location.assign(path); },
        });
        return;
      }
      if (result.needsAuth) {
        mountLoginGate(appHost, {
          teamName: null,
          apiBase: API_BASE,
          context: 'portal',
          onAuthed: async () => { appHost.innerHTML = ''; await renderSetlist(); },
        });
        return;
      }
      if (result.notFound) {
        appHost.innerHTML = `<p class="library-error">${t('setlist.notFound')}</p>`;
        return;
      }
      appHost.innerHTML = `<p class="library-error">${t('setlist.loadError')}</p>`;
    };
    await renderSetlist();
    return { unmount: () => { appHost.innerHTML = ''; exitFullBleedView(); } };
  }

  const shareId = parseShareIdFromPath(location.pathname);
  if (!shareId) {
    renderInvalidShareId(appHost);
    const error = {
      kind: 'invalid-share-id',
      message: 'no shareId in path',
    };
    setState({ error });
    return { error };
  }

  // The API base for gated manifest fetches. Same-origin in production
  // (worker intercepts /api/shares/*). Tests stub global.fetch so any URL
  // ending in manifest.json is served correctly regardless of base.
  const API_BASE = '';

  // ── Gated manifest fetch with gate-state handling ──────────────────────
  // Helper that fetches the manifest and returns { manifest, playbackToken }
  // or handles the gate states (needs-auth → login gate; revoked/error → error screen).
  // Returns null when boot should be aborted (gate displayed / error shown).
  type ManifestData = { manifest: ReturnType<typeof Object.create>; playbackToken: string | null };

  async function fetchManifestGated(): Promise<ManifestData | null> {
    const result = await loadManifest(shareId, API_BASE);
    if (result.state === 'ok') {
      return { manifest: result.manifest, playbackToken: result.playbackToken };
    }
    if (result.state === 'needs-auth') {
      // Show login gate. After auth, retry manifest fetch.
      mountLoginGate(appHost, {
        teamName: result.teamName,
        apiBase: API_BASE,
        onAuthed: async () => {
          appHost.innerHTML = '';
          const data = await fetchManifestGated();
          if (data) {
            await mountPlayer(data.manifest, data.playbackToken);
          }
        },
      });
      return null;
    }
    if (result.state === 'revoked') {
      const error = { kind: 'revoked', message: 'Доступ к этой записи закрыт' };
      setState({ error });
      renderErrorScreen(appHost, error);
      return null;
    }
    // state === 'error'
    const status = result.status;
    const error = {
      kind: status === 404 ? 'not-found' : 'load-failed',
      message: `manifest fetch failed: ${status}`,
    };
    setState({ error });
    renderErrorScreen(appHost, error);
    return null;
  }

  // Helper that builds a gated fetch function for peaks/audio.
  // When a playback token is available, fetches go through the worker with
  // Authorization: Bearer pb:<token>. On 401, refreshes the token once.
  // When no token is available (legacy / test stub), falls back to direct CDN.
  function makeGatedFetch(
    playbackToken: string | null,
    onTokenRefresh: (newToken: string | null) => void,
  ): typeof fetchImpl {
    if (!playbackToken) {
      // No token → legacy CDN path (backward compat: tests/public bundles).
      return fetchImpl;
    }
    let currentToken = playbackToken;
    return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = typeof input === 'string' ? input : input.toString();
      const res = await fetchImpl(url, {
        ...init,
        headers: {
          ...(init?.headers ?? {}),
          Authorization: `Bearer pb:${currentToken}`,
        },
      });
      if (res.status === 401) {
        // Token expired — refresh once via loadManifest.
        const refreshed = await loadManifest(shareId, API_BASE);
        if (refreshed.state === 'ok' && refreshed.playbackToken) {
          currentToken = refreshed.playbackToken;
          onTokenRefresh(currentToken);
          // Retry with new token.
          return fetchImpl(url, {
            ...init,
            headers: {
              ...(init?.headers ?? {}),
              Authorization: `Bearer pb:${currentToken}`,
            },
          });
        }
        // Still unauthorized — show login gate and abort the current mount.
        if (refreshed.state === 'needs-auth') {
          mountLoginGate(appHost, {
            teamName: refreshed.teamName,
            apiBase: API_BASE,
            onAuthed: () => {
              // Reload the page to re-run boot with fresh cookie.
              if (typeof location !== 'undefined') location.reload();
            },
          });
          // Throw a sentinel so mountPlayer's catch does NOT render the error
          // screen on top of the gate we just mounted.
          throw new GateShownError();
        }
      }
      return res;
    };
  }

  // Mount the player once we have a manifest and optional playback token.
  let bootResult: BootResult | null = null;
  async function mountPlayer(manifest: any, playbackToken: string | null): Promise<void> {
    let currentToken = playbackToken;

    // Peaks: if we have a token, fetch through worker; otherwise use legacy CDN.
    let peaks;
    let perTrackPeaks;
    try {
      if (currentToken) {
        // Fetch peaks through the worker gate with pb token.
        const gatedFetch = makeGatedFetch(currentToken, (t) => { currentToken = t; });
        const peaksUrl = manifest.peaksUrl ?? 'peaks.json';
        const peaksRes = await gatedFetch(`${API_BASE}/api/shares/${shareId}/${peaksUrl}`);
        if (!peaksRes.ok) throw new Error(`peaks fetch failed: ${peaksRes.status}`);
        peaks = await peaksRes.json();
        // Per-track peaks.
        if (manifest.v >= 2 && manifest.perTrackPeaksUrl) {
          try {
            const ptRes = await gatedFetch(`${API_BASE}/api/shares/${shareId}/${manifest.perTrackPeaksUrl}`);
            perTrackPeaks = ptRes.ok ? await ptRes.json() : null;
          } catch { perTrackPeaks = null; }
        } else {
          perTrackPeaks = null;
        }
      } else {
        // Legacy CDN path (no token — tests/public bundles without D1 row).
        [peaks, perTrackPeaks] = await Promise.all([
          loadPeaks(cdnBase, shareId, manifest.peaksUrl),
          loadPerTrackPeaks(cdnBase, shareId, manifest),
        ]);
      }
    } catch (err) {
      // GateShownError means the login gate is already mounted — do not
      // render the error screen on top of it.
      if (err instanceof GateShownError) return;
      const message = err instanceof Error ? err.message : String(err);
      const error = {
        kind: isNotFoundError(message) ? 'not-found' : 'load-failed',
        message,
      };
      setState({ error });
      renderErrorScreen(appHost, error);
      return;
    }

    setState({ manifest, peaks });

    const audioContext = (opts.audioContextFactory ?? (() => new AudioContext()))();

    const player: Player = createPlayer({
      audioContext,
      manifest,
      fetchAudio: async (url) => {
        if (currentToken) {
          // Route through worker with pb token.
          const gatedFetch = makeGatedFetch(currentToken, (t) => { currentToken = t; });
          const res = await gatedFetch(`${API_BASE}/api/shares/${shareId}/${url}`);
          if (!res.ok) throw new Error(`audio fetch failed: ${res.status}`);
          return res.arrayBuffer();
        }
        // Legacy CDN path (no token).
        const fullUrl = `${cdnBase}/shares/${shareId}/${url}`;
        const res = await fetchImpl(fullUrl);
        if (!res.ok) throw new Error(`audio fetch failed: ${res.status}`);
        return res.arrayBuffer();
      },
    });

    // Mirror player events into shared state so every panel reacts via the
    // same subscription mechanism.
    const unsubPlayer = player.subscribe((event) => {
      if (event.type === 'loaded') {
        setState({
          loaded: true,
          trackStates: new Map(player.getTrackStates()),
        });
      } else if (event.type === 'playing') {
        setState({ playing: true });
      } else if (event.type === 'paused') {
        setState({ playing: false });
      } else if (event.type === 'ended') {
        setState({ playing: false, playhead: 0 });
      }
    });

    // Periodic playhead sync so timeline/transport re-render from state.
    let rafHandle: number | null = null;
    const rafImpl: typeof requestAnimationFrame =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame.bind(globalThis)
        : ((cb: FrameRequestCallback) =>
            setTimeout(() => cb(Date.now()), 16) as unknown as number);
    const cancelRafImpl: typeof cancelAnimationFrame =
      typeof cancelAnimationFrame === 'function'
        ? cancelAnimationFrame.bind(globalThis)
        : ((id: number) => clearTimeout(id as unknown as ReturnType<typeof setTimeout>));

    const tickPlayhead = (): void => {
      setState({ playhead: player.getPlayhead() });
      rafHandle = rafImpl(tickPlayhead);
    };
    rafHandle = rafImpl(tickPlayhead);

    // Mount UI panels.
    const topbar = mountTopbar(byId('share-topbar'));
    const sidebar = mountSetlistSidebar(byId('share-rail'), manifest);

    // 53-я drawer backdrop (mobile only — toggles body.rail-open class to lock scroll).
    const backdrop = document.createElement('div');
    backdrop.className = 'rail-backdrop';
    backdrop.addEventListener('click', () => setState({ sidebarOpen: false }));
    document.body.appendChild(backdrop);

    const unsubBackdrop = subscribe((s) => {
      if (s.sidebarOpen) document.body.classList.add('rail-open');
      else document.body.classList.remove('rail-open');
    });

    const playerPanel = mountPlayerPanel(byId('share-player-panel'), manifest, peaks, player, perTrackPeaks);
    const takesSidebar = mountTakesSidebar(byId('share-takes-sidebar'));
    const footer = mountPromoFooter(byId('share-promo-footer'));
    const promoModal = mountPromoModal(byId('share-promo-modal-host'));
    const shareModal = mountShareModal(byId('share-share-modal-host'));

    // Tier 2/3 section gestures — mount on .rh-overview-band (built by
    // mountTimeline inside playerPanel). Sections live in `.rh-sections`
    // layer inside; click/dblclick/longpress bubble up to overview-band.
    const overviewBand = document.querySelector<HTMLElement>('.rh-overview-band');
    const sectionsById = new Map(manifest.sections.map((s: any) => [s.id, s]));
    let sectionHandlers: { unmount(): void } | null = null;
    let loopIndicator: { setActive(id: string | null): void; unmount(): void } | null = null;
    let unsubLoop: (() => void) | null = null;
    if (overviewBand) {
      sectionHandlers = mountSectionHandlers(overviewBand, manifest.sections, {
        onSeek: (timeSec) => player.seek(timeSec),
        onToggleLoop: (sectionId) => {
          const current = getState().activeLoopId;
          if (sectionId === '' || sectionId === current) {
            setState({ activeLoopId: null });
            player.setActiveLoopSection(null);
            return;
          }
          const sec = sectionsById.get(sectionId);
          if (!sec) return;
          setState({ activeLoopId: sectionId });
          player.setActiveLoopSection({ startSec: sec.startSec, endSec: sec.endSec });
        },
      });
      loopIndicator = mountLoopIndicator(overviewBand);
      unsubLoop = subscribe((s) => loopIndicator?.setActive(s.activeLoopId));
    }

    // Kick off audio loading.
    player.load().catch((err) => {
      const message = err instanceof Error ? err.message : String(err);
      setState({ error: { kind: 'audio-load-failed', message } });
    });

    appHost.setAttribute('data-ready', 'true');

    bootResult = {
      unmount: () => {
        if (rafHandle !== null) {
          cancelRafImpl(rafHandle);
          rafHandle = null;
        }
        unsubPlayer();
        unsubBackdrop();
        unsubLoop?.();
        sectionHandlers?.unmount();
        loopIndicator?.unmount();
        backdrop.remove();
        document.body.classList.remove('rail-open');
        topbar.unmount();
        sidebar.unmount();
        playerPanel.unmount();
        takesSidebar.unmount();
        footer.unmount();
        promoModal.unmount();
        shareModal.unmount();
      },
    };
  }

  // ── Boot sequence ─────────────────────────────────────────────────────────
  const manifestData = await fetchManifestGated();
  if (!manifestData) {
    // Gate displayed (needs-auth, revoked, or error screen) — return a
    // synthetic error result so callers can detect boot did not complete.
    const currentError = { kind: 'gate-displayed', message: 'auth gate shown or error rendered' };
    return { error: currentError };
  }

  await mountPlayer(manifestData.manifest, manifestData.playbackToken);
  return bootResult!;
}

// `getState` is re-exported only to silence the unused-import linter; the
// orchestrator itself doesn't call it (state flows in one direction here).
// Keeping the import live lets future event handlers reach for the current
// snapshot without re-wiring the module graph.
void getState;

// Auto-boot on page load. Tests set the marker before importing the module
// so `boot()` can be driven explicitly with mocks.
if (!(globalThis as { __SHARE_WEB_TEST__?: boolean }).__SHARE_WEB_TEST__) {
  boot().catch((err) => {
    // eslint-disable-next-line no-console
    console.error('[share-web] boot failed:', err);
  });
}
